/**
 * Générateur — Comparer deux nombres décimaux
 * Attendu ID : 604 | Niveau : 6ème | Domaine : Nombres et calculs
 * Cluster 4 — Comparer / Ordonner des décimaux
 *
 * Types générés :
 *   compareSigne_qcm_unique, compareSigne_texte, compareSigne_texte_trou_drag
 *   compareMixte_qcm_unique, compareMixte_texte
 *   jugerVF_tableau_vf, jugerVF_qcm_multiple
 *   compareDroite_qcm_unique, compareDroite_texte_trou_drag
 *   cheminDecroissant_qcm_unique
 *
 * Labels EXERCICES_REFERENCE.md :
 *   - Compléter avec <, > ou = entre deux décimaux
 *   - Comparer une écriture à virgule et une écriture fractionnaire
 *   - Juger des comparaisons Vrai ou Faux
 *   - Comparer à l'aide d'une demi-droite graduée
 *   - Trouver un chemin décroissant dans une grille
 *
 * Variantes estimées : ~600+ combinaisons
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 604;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Labels ───
const L_SIGNE  = 'Compléter avec <, > ou = entre deux décimaux';
const L_MIXTE  = "Comparer une écriture à virgule et une écriture fractionnaire";
const L_VF     = 'Juger des comparaisons Vrai ou Faux';
const L_DROITE = "Comparer à l'aide d'une demi-droite graduée";
const L_CHEMIN = "Trouver un chemin décroissant dans une grille";

// ─── Helpers spécifiques ───
const parseFr = (s) => parseFloat(String(s).replace(',', '.'));
const compareDec = (a, b) => {
    const da = parseFr(a), db = parseFr(b);
    if (Math.abs(da - db) < 1e-9) return '=';
    return da < db ? '<' : '>';
};

// ─── Pool de couples de comparaison (pièges ciblés) ───
// E1 = parties décimales comparées comme entiers (1,9 < 1,12 car 9<12)
// E2 = zéros non significatifs rendent plus grand
// E3 = zéros intercalaires ignorés
// E5 = égalité d'écritures non reconnue
// E12 = partie entière comparée à une partie décimale
const COUPLES_COMPARAISON = [
    // E1
    ['1,9', '1,12'], ['1,12', '1,9'], ['4,88', '4,9'], ['4,9', '4,88'],
    ['0,705', '0,72'], ['13,8', '13,15'], ['18,8', '18,12'],
    ['3,12', '3,2'], ['7,5', '7,49'], ['0,9', '0,12'], ['21,25', '21,2'],
    ['6,91', '16,1'], ['5,05', '5,4'], ['0,609', '0,62'],
    // E2 / E5
    ['5,3', '5,30'], ['67,3', '67,30'], ['8,4', '8,40'], ['2,4', '2,40'],
    ['0,5', '0,50'], ['12,9', '12,90'], ['0,060', '0,06'],
    // E3
    ['0,062', '0,0602'], ['0,108', '0,18'], ['0,032', '0,1'],
    ['0,045', '0,45'], ['0,0909', '0,09'],
    // E12
    ['15,2', '16'], ['9', '9,01'], ['12', '11,9'], ['89', '98'],
    // Cas "normaux" (pour diversité)
    ['1,807', '2,601'], ['14,7161', '15,38'], ['69,82', '69,646'],
    ['37,3', '37,1'], ['93,29', '93,33'], ['0,19', '0,121'],
];

const randCouple = () => {
    // 40% pool curé (pièges E1/E2/E3/E5/E12), 60% algorithmique (piège E1 infini)
    if (Math.random() < 0.40) {
        return randomChoice(COUPLES_COMPARAISON);
    }
    // Génération : même partie entière, parties décimales de longueurs différentes
    const entier = randInt(0, 99);
    const dec1 = randInt(1, 9);             // 1 chiffre
    const dec2 = randInt(10, 99);           // 2 chiffres
    return Math.random() < 0.5
        ? [`${entier},${dec1}`, `${entier},${dec2}`]
        : [`${entier},${dec2}`, `${entier},${dec1}`];
};

// ────────────────────────────────────────────────────────────────
// compareSigne
// ────────────────────────────────────────────────────────────────

const genCompareSigneQcm = () => {
    const [a, b] = randCouple();
    const correct = compareDec(a, b);
    const choix = ['<', '>', '='];
    const texte = `<p>Complète avec le bon signe : <strong>${a} … ${b}</strong></p>`;
    return makeQcm(choix, correct, texte, opts(L_SIGNE));
};

const genCompareSigneTexte = () => {
    const [a, b] = randCouple();
    const correct = compareDec(a, b);
    const texte = `<p>Complète avec <code>&lt;</code>, <code>&gt;</code> ou <code>=</code> : <strong>${a} … ${b}</strong></p>`;
    return makeSaisie(correct, texte, { ...opts(L_SIGNE), strict: true });
};

const genCompareSigneDrag = () => {
    const [a, b] = randCouple();
    const correct = compareDec(a, b);
    const autres = ['<', '>', '='].filter(s => s !== correct);
    const phrase = `${a} {{${correct}}} ${b}`;
    const texte = '<p>Glisse le bon signe entre les deux nombres.</p>';
    return makeTexteTrou(phrase, texte, {
        ...opts(L_SIGNE),
        mode: 'drag',
        distracteurs: autres,
        strict: true,
    });
};

// ────────────────────────────────────────────────────────────────
// compareMixte — virgule vs fraction décimale / décomposition
// ────────────────────────────────────────────────────────────────

// Chaque entrée contient une expression KaTeX de la forme "a \\; \\square \\; b"
// où \square est le blanc à compléter par <, > ou =
const PAIRS_MIXTES = [
    { katex: '8{,}74 \\; \\square \\; \\dfrac{874}{100}',                 signe: '=' },
    { katex: '8{,}74 \\; \\square \\; \\dfrac{847}{100}',                 signe: '>' },
    { katex: '\\dfrac{847}{100} \\; \\square \\; 8{,}74',                 signe: '<' },
    { katex: '3 + \\dfrac{12}{100} \\; \\square \\; 3{,}12',              signe: '=' },
    { katex: '3 + \\dfrac{9}{100} \\; \\square \\; 3{,}9',                signe: '<' }, // 3,09 < 3,9
    { katex: '12 + \\dfrac{9}{100} \\; \\square \\; 12{,}9',              signe: '<' }, // 12,09 < 12,9
    { katex: '\\dfrac{7}{10} + \\dfrac{4}{100} \\; \\square \\; 0{,}47',  signe: '>' }, // 0,74 > 0,47
    { katex: '\\dfrac{7}{10} + \\dfrac{4}{100} \\; \\square \\; 0{,}74',  signe: '=' },
    { katex: '\\dfrac{25}{100} \\; \\square \\; 0{,}25',                  signe: '=' },
    { katex: '\\dfrac{3}{10} \\; \\square \\; 0{,}03',                    signe: '>' }, // 0,3 > 0,03
    { katex: '5 + \\dfrac{4}{10} \\; \\square \\; \\dfrac{54}{10}',       signe: '=' }, // 5,4 = 5,4
    { katex: '\\dfrac{125}{1000} \\; \\square \\; 0{,}125',               signe: '=' },
    { katex: '\\dfrac{6}{10} \\; \\square \\; \\dfrac{60}{100}',          signe: '=' },
    { katex: '2 + \\dfrac{5}{10} + \\dfrac{3}{100} \\; \\square \\; 2{,}53', signe: '=' },
    { katex: '\\dfrac{18}{10} \\; \\square \\; 1{,}08',                   signe: '>' }, // 1,8 > 1,08
    // ─── Extensions : équivalences directes, décompositions, pièges rang ───
    { katex: '\\dfrac{357}{100} \\; \\square \\; 3{,}57',                 signe: '=' },
    { katex: '\\dfrac{42}{10} \\; \\square \\; 4{,}2',                    signe: '=' },
    { katex: '\\dfrac{89}{1000} \\; \\square \\; 0{,}089',                signe: '=' },
    { katex: '0{,}625 \\; \\square \\; \\dfrac{625}{1000}',               signe: '=' },
    { katex: '4 + \\dfrac{6}{10} + \\dfrac{8}{100} \\; \\square \\; 4{,}68', signe: '=' },
    { katex: '3{,}89 \\; \\square \\; \\dfrac{389}{10}',                  signe: '<' }, // 3,89 < 38,9
    { katex: '7 + \\dfrac{2}{100} \\; \\square \\; 7{,}2',                signe: '<' }, // 7,02 < 7,2
    { katex: '\\dfrac{4}{100} \\; \\square \\; \\dfrac{4}{10}',           signe: '<' }, // 0,04 < 0,4
    { katex: '\\dfrac{52}{100} \\; \\square \\; 5{,}2',                   signe: '<' }, // 0,52 < 5,2
    { katex: '\\dfrac{6}{1000} \\; \\square \\; \\dfrac{6}{100}',         signe: '<' }, // 0,006 < 0,06
    { katex: '5{,}62 \\; \\square \\; \\dfrac{526}{1000}',                signe: '>' }, // 5,62 > 0,526
    { katex: '0{,}405 \\; \\square \\; \\dfrac{45}{1000}',                signe: '>' }, // 0,405 > 0,045
    { katex: '0{,}8 \\; \\square \\; \\dfrac{8}{100}',                    signe: '>' }, // 0,8 > 0,08
    { katex: '1{,}5 \\; \\square \\; \\dfrac{15}{100}',                   signe: '>' }, // 1,5 > 0,15
    { katex: '\\dfrac{25}{10} \\; \\square \\; 2{,}49',                   signe: '>' }, // 2,5 > 2,49
];

const genCompareMixteQcm = () => {
    const m = randomChoice(PAIRS_MIXTES);
    const choix = ['<', '>', '='];
    const texte = '<p>Complète avec le bon signe : {{element:0}}</p>';
    const p = makeQcm(choix, m.signe, texte, opts(L_MIXTE));
    p.elements_additionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: m.katex } }],
    };
    return p;
};

const genCompareMixteTexte = () => {
    const m = randomChoice(PAIRS_MIXTES);
    const texte = '<p>Complète avec <code>&lt;</code>, <code>&gt;</code> ou <code>=</code> : {{element:0}}</p>';
    const p = makeSaisie(m.signe, texte, { ...opts(L_MIXTE), strict: true });
    p.elements_additionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: m.katex } }],
    };
    return p;
};

// ────────────────────────────────────────────────────────────────
// jugerVF
// ────────────────────────────────────────────────────────────────

// Construit 1 ligne : {lignes, br} où br = 0(Vrai) ou 1(Faux)
const buildLigneVF = ([a, b]) => {
    const actual = compareDec(a, b);
    // 50% vraie, 50% fausse
    const vraie = Math.random() < 0.5;
    const displayed = vraie
        ? actual
        : randomChoice(['<', '>', '='].filter(s => s !== actual));
    return { ligne: `${a} ${displayed} ${b}`, br: vraie ? 0 : 1 };
};

const genJugerVFTableau = () => {
    const nLines = randInt(6, 8);
    const couples = randomChoiceMultiple(COUPLES_COMPARAISON, nLines);
    const built = couples.map(buildLigneVF);
    const lignes = built.map(b => b.ligne);
    const bonnesReponses = built.map(b => b.br);
    const texte = '<p>Pour chaque comparaison, indique si elle est <strong>vraie</strong> ou <strong>fausse</strong>.</p>';
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
};

const genJugerVFQcmMulti = () => {
    // Loop jusqu'à obtenir 2-4 vraies parmi 5
    for (let tries = 0; tries < 20; tries++) {
        const couples = randomChoiceMultiple(COUPLES_COMPARAISON, 5);
        const enonces = [];
        const vrais = [];
        couples.forEach(([a, b]) => {
            const actual = compareDec(a, b);
            const vraie = Math.random() < 0.5;
            const displayed = vraie
                ? actual
                : randomChoice(['<', '>', '='].filter(s => s !== actual));
            const e = `${a} ${displayed} ${b}`;
            enonces.push(e);
            if (vraie) vrais.push(e);
        });
        if (vrais.length >= 2 && vrais.length <= 4) {
            const texte = '<p>Parmi ces comparaisons, <strong>lesquelles sont vraies</strong> ? (plusieurs réponses possibles)</p>';
            return makeQcm(enonces, vrais, texte, opts(L_VF), true);
        }
    }
    // Fallback : forcer 3 vraies
    const couples = randomChoiceMultiple(COUPLES_COMPARAISON, 5);
    const enonces = couples.map(([a, b], i) => {
        const actual = compareDec(a, b);
        const vraie = i < 3;
        const displayed = vraie ? actual : randomChoice(['<', '>', '='].filter(s => s !== actual));
        return { e: `${a} ${displayed} ${b}`, vraie };
    });
    const texte = '<p>Parmi ces comparaisons, <strong>lesquelles sont vraies</strong> ? (plusieurs réponses possibles)</p>';
    return makeQcm(
        enonces.map(x => x.e),
        enonces.filter(x => x.vraie).map(x => x.e),
        texte, opts(L_VF), true
    );
};

// ────────────────────────────────────────────────────────────────
// compareDroite
// ────────────────────────────────────────────────────────────────

// Génère une config (start, end, step, a, b) pour la droite graduée
const randNumberlineConfig = () => {
    const centiemes = Math.random() < 0.5;
    if (centiemes) {
        const base10 = randInt(0, 98); // 0..9,8
        const start = parseFloat((base10 / 10).toFixed(1));
        const end = parseFloat((start + 0.1).toFixed(1));
        const step = 0.01;
        let ai = randInt(1, 9), bi = randInt(1, 9);
        while (bi === ai) bi = randInt(1, 9);
        const a = parseFloat((start + ai * 0.01).toFixed(2));
        const b = parseFloat((start + bi * 0.01).toFixed(2));
        return { start, end, step, a, b };
    } else {
        const start = randInt(0, 9);
        const end = start + 1;
        const step = 0.1;
        let ai = randInt(1, 9), bi = randInt(1, 9);
        while (bi === ai) bi = randInt(1, 9);
        const a = parseFloat((start + ai * 0.1).toFixed(1));
        const b = parseFloat((start + bi * 0.1).toFixed(1));
        return { start, end, step, a, b };
    }
};

const buildNumberlineElement = ({ start, end, step, a, b }, labels = ['A', 'B']) => ({
    type: 'numberline',
    config: {
        start, end, step,
        lineType: 'half',
        showArrow: true,
        showStart: true,
        showEnd: true,
        points: [
            { value: a, label: labels[0], showValue: false, color: '#475569' },
            { value: b, label: labels[1], showValue: false, color: '#475569' },
        ],
    },
});

const genCompareDroiteQcm = () => {
    const cfg = randNumberlineConfig();
    const correct = cfg.a < cfg.b ? '<' : (cfg.a > cfg.b ? '>' : '=');
    const choix = ['<', '>', '='];
    const texte = '<p>Sur cette demi-droite graduée, les points A et B ont été placés. Compare leurs abscisses : <strong>A … B</strong></p>{{element:0}}';
    const p = makeQcm(choix, correct, texte, opts(L_DROITE));
    p.elements_additionnels = { version: 1, elements: [buildNumberlineElement(cfg)] };
    return p;
};

const genCompareDroiteDrag = () => {
    const cfg = randNumberlineConfig();
    // Pour varier, on peut utiliser C et D
    const labels = Math.random() < 0.5 ? ['A', 'B'] : ['C', 'D'];
    const correct = cfg.a < cfg.b ? '<' : (cfg.a > cfg.b ? '>' : '=');
    const autres = ['<', '>', '='].filter(s => s !== correct);
    const phrase = `${labels[0]} {{${correct}}} ${labels[1]}`;
    const texte = '<p>Observe les points placés sur la droite graduée, puis glisse le bon signe.</p>{{element:0}}';
    const p = makeTexteTrou(phrase, texte, {
        ...opts(L_DROITE),
        mode: 'drag',
        distracteurs: autres,
        strict: true,
    });
    p.elements_additionnels = { version: 1, elements: [buildNumberlineElement(cfg, labels)] };
    return p;
};

// ────────────────────────────────────────────────────────────────
// cheminDecroissant
// ────────────────────────────────────────────────────────────────

// Cases générées PAR CONSTRUCTION pour activer deux misconceptions classiques
// (enrichissement 2026-07-04, remplace le pool de 12 cases figées) :
//  (E1) La BONNE réponse exploite "plus de chiffres = plus grand" à l'envers :
//       ex current=12,5 → correct=12,48 (12,48 < 12,50 réellement, mais un
//       élève qui lit "48 > 5" croit 12,48 > 12,5 et rejette à tort la bonne).
//  (E2) Les distracteurs sont des dixièmes > current (même entier, ou entier
//       suivant), qui semblent "encore plus petits" que correct pour un élève
//       en erreur : ex current=7,2 → distracteur=7,3 (7,3 > 7,2 réellement,
//       mais l'élève qui compare "3 < 19" croit 7,3 < 7,19 < 7,2 et le choisit).
// Le 4ème choix "Il n'y a pas de bonne réponse" piège les élèves qui rejettent
// correct (à cause de E1) et ne trouvent plus aucune option "plus petite".
//
// Unicité de la bonne réponse PAR CONSTRUCTION : correct = e + (10d−k)/100 est
// STRICTEMENT inférieur à current = e + d/10 (k ≥ 1), et les deux distracteurs
// sont STRICTEMENT supérieurs à current — une seule case "plus petite" possible.
const genCheminCase = () => {
    const e = randInt(2, 24);            // partie entière de la case courante
    const d = randInt(2, 8);             // chiffre des dixièmes de la case courante
    const k = randomChoice([1, 2]);      // correct = …,(10d−1) ou …,(10d−2) → ex. 12,49 / 12,48
    const current = `${e},${d}`;
    const correct = `${e},${d * 10 - k}`;
    const dist1 = `${e},${d + 1}`;
    const dist2 = d + 2 <= 9
        ? `${e},${randInt(d + 2, 9)}`    // même entier, dixième plus haut
        : `${e + 1},${randInt(1, 5)}`;   // franchit l'entier (ex. 6,8 → 7,3)
    return { current, correct, dist: [dist1, dist2] };
};

const CHEMIN_NO_ANSWER = "Il n'y a pas de bonne réponse";

/** SVG pyramide : la case courante (bleue) en haut, les 3 cases candidates
 *  en dessous, reliées par des flèches. L'ordre d'affichage des candidates est
 *  mélangé indépendamment de l'ordre des choix du QCM (aucun indice visuel). */
const buildCheminSvg = (current, casesBasses) => {
    const W = 320, H = 148, caseW = 84, caseH = 40, r = 10;
    const topX = (W - caseW) / 2, topY = 10, bottomY = 96;
    const xs = [14, (W - caseW) / 2, W - caseW - 14];
    const fleches = xs.map(x =>
        `<line x1="${topX + caseW / 2}" y1="${topY + caseH + 3}" x2="${x + caseW / 2}" y2="${bottomY - 5}" stroke="#94a3b8" stroke-width="1.5" marker-end="url(#fl604)"/>`
    ).join('\n  ');
    const cases = casesBasses.map((v, i) =>
        `<rect x="${xs[i]}" y="${bottomY}" width="${caseW}" height="${caseH}" rx="${r}" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.5"/>
  <text x="${xs[i] + caseW / 2}" y="${bottomY + caseH / 2 + 6}" text-anchor="middle" font-family="Arial, sans-serif" font-size="17" fill="#1e293b">${v}</text>`
    ).join('\n  ');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">
  <defs><marker id="fl604" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0 0 L6 3 L0 6 Z" fill="#94a3b8"/></marker></defs>
  <rect x="${topX}" y="${topY}" width="${caseW}" height="${caseH}" rx="${r}" fill="#1E3A8A"/>
  <text x="${W / 2}" y="${topY + caseH / 2 + 6}" text-anchor="middle" font-family="Arial, sans-serif" font-size="17" font-weight="bold" fill="#ffffff">${current}</text>
  ${fleches}
  ${cases}
</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const genCheminDecroissantQcm = () => {
    const c = genCheminCase();
    const choix = [c.correct, ...c.dist, CHEMIN_NO_ANSWER];
    const svgUrl = buildCheminSvg(c.current, shuffle([c.correct, ...c.dist]));
    const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };
    const texte = `<p>Tu es sur la case <strong>${c.current}</strong>. Tu dois avancer vers une case contenant un nombre <strong>strictement plus petit</strong>. Quelle case peux-tu choisir ?</p>`;
    return makeQcm(choix, c.correct, texte, { ...opts(L_CHEMIN), elements_additionnels: elAdd });
};

// ────────────────────────────────────────────────────────────────
// Registre
// ────────────────────────────────────────────────────────────────

const GENERATORS = {
    compareSigne_qcm_unique:       genCompareSigneQcm,
    compareSigne_texte:            genCompareSigneTexte,
    compareSigne_texte_trou_drag:  genCompareSigneDrag,
    compareMixte_qcm_unique:       genCompareMixteQcm,
    compareMixte_texte:            genCompareMixteTexte,
    jugerVF_tableau_vf:            genJugerVFTableau,
    jugerVF_qcm_multiple:          genJugerVFQcmMulti,
    compareDroite_qcm_unique:      genCompareDroiteQcm,
    compareDroite_texte_trou_drag: genCompareDroiteDrag,
    cheminDecroissant_qcm_unique:  genCheminDecroissantQcm,
};

// ────────────────────────────────────────────────────────────────
// CATEGORIES (affichage des cards)
// ────────────────────────────────────────────────────────────────

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'compareSigne',      label: L_SIGNE,  formatsList: buildFormats('compareSigne') },
    { id: 'compareMixte',      label: L_MIXTE,  formatsList: buildFormats('compareMixte') },
    { id: 'jugerVF',           label: L_VF,     formatsList: buildFormats('jugerVF') },
    { id: 'compareDroite',     label: L_DROITE, formatsList: buildFormats('compareDroite') },
    { id: 'cheminDecroissant', label: L_CHEMIN, formatsList: buildFormats('cheminDecroissant') },
];

// ────────────────────────────────────────────────────────────────
// Point d'entrée
// ────────────────────────────────────────────────────────────────

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            k => k.startsWith(typeExercice) || k.includes(typeExercice)
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
